import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import api from '../../services/api';
import './BridgeClubPages.css';

interface GrowthData {
  date: string;
  new_members: number;
  total_members: number;
}

interface RevenueData {
  month: string;
  revenue: number;
  purchases: number;
}

interface StoreData {
  name: string;
  value: number;
  [key: string]: string | number;
}

interface ProductData {
  name: string;
  purchase_count: number;
  total_bags: number;
  revenue: number;
}

interface RetentionData {
  active_rate: number;
  avg_purchases_per_member: number;
  avg_lifetime_value: number;
  repeat_rate: number;
}

interface AnalyticsData {
  member_growth: GrowthData[];
  revenue_by_month: RevenueData[];
  purchases_by_store: StoreData[];
  top_products: ProductData[];
  retention: RetentionData;
}

const COLORS = ['#8B0000', '#1a472a', '#B8860B', '#3B82F6', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

type PeriodType = '1month' | '3months' | '6months' | '1year' | 'all';

const BridgeClubAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodType>('3months');
  const [data, setData] = useState<AnalyticsData>({
    member_growth: [],
    revenue_by_month: [],
    purchases_by_store: [],
    top_products: [],
    retention: {
      active_rate: 0,
      avg_purchases_per_member: 0,
      avg_lifetime_value: 0,
      repeat_rate: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<AnalyticsData>(`/admin/bridge-club/analytics-detail/?period=${period}`);
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
            &larr; Back to Bridge Club
          </button>
          <h1>Bridge Club Analytics</h1>
          <p>Program performance and insights</p>
        </div>
        <div className="period-selector">
          <select value={period} onChange={e => setPeriod(e.target.value as PeriodType)}>
            <option value="1month">This Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">This Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchAnalytics}>Retry</button>
        </div>
      )}

      {/* Retention Metrics */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{data.retention.active_rate}%</div>
          <div className="stat-label">Active Rate (60 days)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.retention.avg_purchases_per_member}</div>
          <div className="stat-label">Avg Purchases/Member</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(data.retention.avg_lifetime_value)}</div>
          <div className="stat-label">Avg Lifetime Value</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.retention.repeat_rate}%</div>
          <div className="stat-label">Repeat Purchase Rate</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        {/* Member Growth Chart */}
        <div className="chart-card">
          <h3>Member Growth</h3>
          {data.member_growth.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.member_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total_members"
                  stroke="#8B0000"
                  strokeWidth={2}
                  name="Total Members"
                  dot={{ fill: '#8B0000' }}
                />
                <Line
                  type="monotone"
                  dataKey="new_members"
                  stroke="#1a472a"
                  strokeWidth={2}
                  name="New Members"
                  dot={{ fill: '#1a472a' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue Chart */}
        <div className="chart-card">
          <h3>Revenue by Month</h3>
          {data.revenue_by_month.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.revenue_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenue" fill="#1a472a" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="charts-grid">
        {/* Purchases by Store */}
        <div className="chart-card">
          <h3>Purchases by Store</h3>
          {data.purchases_by_store.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.purchases_by_store}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {data.purchases_by_store.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Purchases by Month (Bar) */}
        <div className="chart-card">
          <h3>Purchase Count by Month</h3>
          {data.revenue_by_month.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.revenue_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="purchases" fill="#8B0000" name="Purchases" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Products Table */}
      <div className="chart-card full-width">
        <h3>Top Products Purchased</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Product</th>
                <th>Times Purchased</th>
                <th>Total Bags</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.top_products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-row">
                    No product data available
                  </td>
                </tr>
              ) : (
                data.top_products.map((product, idx) => (
                  <tr key={product.name}>
                    <td className="rank">#{idx + 1}</td>
                    <td>{product.name}</td>
                    <td className="center">{product.purchase_count.toLocaleString()}</td>
                    <td className="center">{product.total_bags.toLocaleString()}</td>
                    <td className="currency">{formatCurrency(product.revenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubAnalytics;
